import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { AuthState, UserProfile } from '../types/auth';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext<{
  authState: AuthState;
  setAuthState: React.Dispatch<React.SetStateAction<AuthState>>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateUserRole: (role: string) => Promise<void>;
  completeOnboarding: () => Promise<boolean>;
}>({
  authState: { user: null, loading: true, error: null },
  setAuthState: () => {},
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  updateUserRole: async () => {},
  completeOnboarding: async () => false,
});

export const AuthProvider: React.FC<{ children: React.ReactNode; inactivityTimeoutMs?: number }> = ({ children, inactivityTimeoutMs = 5 * 60 * 1000 }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });
  const [lastActivity, setLastActivity] = useState(Date.now());
  const navigate = useNavigate();
  const inactivityTimeout = useRef(inactivityTimeoutMs);

  // Update inactivityTimeout if prop changes
  useEffect(() => {
    inactivityTimeout.current = inactivityTimeoutMs;
  }, [inactivityTimeoutMs]);

  // Helper to update last activity
  const updateLastActivity = () => setLastActivity(Date.now());

  const fetchUserProfile = useCallback(async (user: User) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // If no profile row, create it from user.user_metadata if available
      if (error) {
        // Try to create the profile from user.user_metadata
        const meta = user.user_metadata || {};
        console.log('Creating profile with metadata:', meta);
        if (meta.username && meta.role) {
          const now = new Date().toISOString();
          const { error: insertError } = await supabase
            .from('profiles')
            .insert([
              {
                id: user.id,
                username: meta.username,
                role: meta.role,
                state: meta.state || '',
                instagram: meta.instagram || '',
                youtube: meta.youtube || '',
                created_at: now,
                updated_at: now
              }
            ]);
          if (!insertError) {
            // Refetch the profile after creation
            const { data: newProfile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', user.id)
              .single();
            setAuthState({
              user: { ...newProfile, email: user.email, email_confirmed_at: user.email_confirmed_at } as UserProfile,
              loading: false,
              error: null,
            });
            return;
          }
        }
        // If we can't create, redirect to profile completion
        console.error('No profile found for user, redirecting to profile completion:', error);
        navigate('/profile', { replace: true });
        setAuthState({
          user: {
            id: user.id,
            email: user.email,
            email_confirmed_at: user.email_confirmed_at,
            username: '',
            role: '',
            state: '',
            created_at: '',
            updated_at: ''
          },
          loading: false,
          error: null,
        });
        return;
      }

      // Check if email is confirmed (Supabase user object has email_confirmed_at)
      if (!user.email_confirmed_at) {
        navigate('/activate', { replace: true });
      }

      setAuthState({
        user: { ...data, email: user.email, email_confirmed_at: user.email_confirmed_at } as UserProfile,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setAuthState({
        user: null,
        loading: false,
        error: error instanceof Error ? error.message : 'Error fetching user profile',
      });
    }
  }, [navigate]);

  // Listen for user activity (not tab focus)
  useEffect(() => {
    const activityEvents = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
    activityEvents.forEach(event => window.addEventListener(event, updateLastActivity));
    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, updateLastActivity));
    };
  }, []);

  // On mount, do initial session check
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        fetchUserProfile(session.user);
      } else {
        setAuthState(prev => ({ ...prev, loading: false }));
      }
    });
  }, [fetchUserProfile]);

  // Keep the realtime socket's JWT in sync with the auth session so long-lived
  // subscriptions (e.g. direct messages) survive token refreshes. Deliberately
  // does not touch authState — existing session handling stays as-is.
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      try {
        const token = session?.access_token;
        Promise.resolve((supabase.realtime as any).setAuth(token)).catch(() => undefined);
      } catch {
        // Realtime auth sync is best-effort.
      }
    });
    return () => {
      listener?.subscription?.unsubscribe();
    };
  }, []);

  // On tab focus, check session only if inactivity period has passed
  useEffect(() => {
    const handleFocus = () => {
      const now = Date.now();
      if (now - lastActivity > inactivityTimeout.current) {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session?.user) {
            fetchUserProfile(session.user);
          } else {
            setAuthState(prev => ({ ...prev, user: null, loading: false }));
          }
        });
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [lastActivity, fetchUserProfile]);

  // Utility to map Supabase errors to user-friendly messages
  function getFriendlyAuthError(error: any): string {
    if (!error) return 'Unknown error';
    const msg = error.message ? error.message.toLowerCase() : '';
    console.log('Raw error message:', error.message);
    console.log('Lowercase message:', msg);
    if (msg.includes('invalid login credentials') || msg.includes('invalid login credential')) {
      return 'Invalid email or password.';
    }
    if (msg.includes('email not confirmed')) return 'Your email is not confirmed. Please check your inbox.';
    if (msg.includes('network')) return 'Network error. Please try again.';
    if (msg.includes('user not found')) return 'No account found with this email.';
    if (msg.includes('password')) return 'Incorrect password.';
    return error.message || 'An unknown error occurred.';
  }

  const signIn = async (email: string, password: string) => {
    try {
      console.log('Starting sign in...');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        console.log('Sign in error received:', error);
        // If the error is 'Email not confirmed', redirect to /activate
        if (error.message && error.message.toLowerCase().includes('email not confirmed')) {
          navigate('/activate', { replace: true });
        }
        // Clear user on failed login and set error
        const friendlyError = getFriendlyAuthError(error);
        console.log('Setting friendly error:', friendlyError);
        setAuthState(prev => ({ ...prev, user: null, error: friendlyError }));
        return; // Don't throw, just return
      } else {
        // On success, fetch and set user profile immediately
        if (data && data.user) {
          await fetchUserProfile(data.user);
        } else {
          setAuthState(prev => ({ ...prev, error: null }));
        }
      }
    } catch (error) {
      console.error('Sign in catch error:', error);
      const friendlyError = getFriendlyAuthError(error);
      console.log('Setting catch error:', friendlyError);
      setAuthState(prev => ({
        ...prev,
        user: null,
        error: friendlyError,
      }));
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      navigate('/activate', { replace: true });
    } catch (error) {
      console.error('Sign up error:', error);
      setAuthState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error signing up',
      }));
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      // Clear user state on sign out
      setAuthState({ user: null, loading: false, error: null });
      // Optionally clear localStorage/sessionStorage if you use custom keys
      // localStorage.removeItem('your_custom_key');
      // sessionStorage.removeItem('your_custom_key');
    } catch (error) {
      console.error('Sign out error:', error);
      setAuthState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error signing out',
      }));
    }
  };

  const updateUserRole = async (role: string) => {
    if (!authState.user) return;

    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('profiles')
        .update({ 
          role,
          updated_at: now
        })
        .eq('id', authState.user.id);

      if (error) throw error;

      setAuthState(prev => ({
        ...prev,
        user: prev.user ? { ...prev.user, role, updated_at: now } as UserProfile : null,
      }));
    } catch (error) {
      console.error('Update role error:', error);
      setAuthState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error updating user role',
      }));
    }
  };

  /**
   * Marks Home onboarding as done. Does not touch authState.error so login/signup
   * flows stay unaffected if the update fails (e.g. migration not applied yet).
   */
  const completeOnboarding = useCallback(async (): Promise<boolean> => {
    if (!authState.user?.id) return false;

    const userId = authState.user.id;
    const now = new Date().toISOString();
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ onboarding_completed_at: now })
        .eq('id', userId);

      if (error) {
        console.error('Complete onboarding error:', error);
        return false;
      }

      setAuthState(prev => ({
        ...prev,
        user: prev.user
          ? { ...prev.user, onboarding_completed_at: now, updated_at: now }
          : null,
      }));
      return true;
    } catch (error) {
      console.error('Complete onboarding error:', error);
      return false;
    }
  }, [authState.user?.id]);

  return (
    <AuthContext.Provider
      value={{ authState, setAuthState, signIn, signUp, signOut, updateUserRole, completeOnboarding }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 